import { z } from "zod";

import http from "@/services/http";

const globalProduct = z
  .object({
    id: z.number(),
    content_bundle_id: z.number(),
    global_product_id: z.number(),
    global_product_name: z.string(),
    created_at: z.string().datetime().nullish(),
    updated_at: z.string().datetime().nullish(),
  })
  .passthrough();

const redeem_exams = z
  .object({
    acronym: z.string().nullish(),
    duration_sec: z.number().nullish(),
    eol_date: z.string().nullish(),
    exam_number: z.string().nullish(),
    guid: z.string().nullish(),
    release_date: z.string().nullish(),
    title: z.string().nullish(),
    type: z.string().nullish(),
    version: z.string().nullish(),
  })
  .passthrough();

const stringOrStringArray = z.union([z.string(), z.array(z.string())]);

const catalogData = z
  .object({
    id: z.number(),
    active: z.number(),
    adjacent_technology: z.string().nullish(),
    acronym: z.string().nullish(),
    author: z.string().nullish(),
    benefits: z.array(z.string()).nullish(),
    ce_credits: z.number().nullish(),
    certification_guids: z.array(z.string()).nullish(),
    content_retired: z.string().nullish(),
    content_type_id: z.number().nullish(),
    copyright_holder: z.string().nullish(),
    copyright_year: z.number().nullish(),
    global_products: z.array(globalProduct).nullish(),
    days_to_retirement: z.number().nullish(),
    duration_offer_in_days: z.number().nullish(),
    data_classification: z.string().nullish(),
    description: z.string().nullish(),
    duration_sec: z.number().nullish(),
    exam_format_id: z.number().nullish(),
    friendly_url: z.string().nullish(),
    guid: z.string(),
    is_partial: z.number().nullish(),
    is_private: z.number().nullish(),
    instructions: stringOrStringArray.nullish(),
    product_alias: z.string().nullish(),
    product_id: z.number().nullish(),
    product_version: z.string().nullish(),
    publisher_name: z.string().nullish(),
    technology: z.string().nullish(),
    title: z.string().nullish(),
    type: z.string(),
    url: z.string().nullish(),
    event_date: z.string().datetime().nullish(),
    associated_descriptions: z.array(z.string()).nullish(),
    associated_titles: z.array(z.string()).nullish(),
    associated_guids: z.array(z.string()).nullish(),
    associated_certifications: z.array(z.string()).nullish(),
    name: z.string().nullish(),
    authoring_platform: z.string().nullish(),
    cisco_be: z.string().nullish(),
    content_bundle: z.array(z.number()).nullish(),
    content_provider: z.string().nullish(),
    presenter: z.string().nullish(),
    publisher_id: z.number().nullish(),
    solutions: z.array(z.string()).nullish(),
    number_of_labs: z.number().nullish(),
    version: z.any().nullish(),
    training_modality: z.string().nullish(),
    job_roles: z.any().nullish(),
    product: z.string().nullish(),
    skills_associated: z
      .array(z.string())
      .nullish()
      .or(z.string().default("").nullish()),
    difficulty_type: z.string().nullish(),
    difficulty_type_id: z.number().nullish(),
    difficulty_types: z.array(z.string()).nullish(),
    difficulty_type_ids: z.array(z.number()).nullish(),
    lab_type: z.string().nullish(),
    lab_type_id: z.number().nullish(),
    modeling_lab_id: z.number().nullish(),
    clc_credits: z.number().nullish(),
    content_guid: z.string().nullish(),
    ga_date: z.string().nullish(),
    eol_date: z.string().nullish(),
    whats_included: z.array(z.string()).nullish(),
    bundle_type: z.string().nullish(),
    eos_date: z.string().nullish(),
    offer_type: z.string().nullish(),
    is_single: z.number().nullish(),
    content_type: z.string().nullish(),
    references: z
      .array(
        z.object({
          id: z.number().nullish(),
          name: z.string().nullish(),
          type: z.string().nullish(),
          url: z.string().nullish(),
        })
      )
      .nullish(),
    summary: z.string().nullish(),
    display_version: z.string().nullish(),
    sort_order: z.number().nullish(),
    authored_at: z.string().nullish(),
    keywords: z.any().nullish(),
    locale_code: z.string().nullish(),
    lock_content: z.boolean().nullish(),
    locale_id: z.number().nullish(),
    certification_level: z.array(z.string()).nullish().or(z.string().nullish()),
    certifications: z.array(z.string()).nullish(),
    content_count: z.number().nullish(),
    number_of_assessments: z.number().nullish(),
    number_of_credits: z.number().nullish(),
    exam_number: z.string().nullish(),
    notes: z.string().nullish(),
    webex_url: z.string().nullish(),
    solution: z.string().nullish(),
    tracks: z.array(z.string()).nullish(),
    created_at: z.string().datetime().nullish(),
    updated_at: z.string().datetime().nullish(),
    deleted_at: z.string().datetime().nullish(),
    redeem_exams: z.array(redeem_exams).nullish(),
  })
  .passthrough();

const recommendationType = z
  .object({
    CourseID: z.string().nullish(),
    CourseTitle: z.string().nullish(),
    CourseType: z.string().nullish(),
    RelevancyScore: z.string().nullish(),
    TrackID: z.string().nullish(),
    Confidence: z.string().nullish(),
    TrackTitle: z.string().nullish(),
    TrackUID: z.string().nullish(),
    Type: z.string().nullish(),
    catalog_data: catalogData,
    recommendationType: z.string().nullish(),
  })
  .passthrough();

const recommendationPath = z
  .object({
    PathID: z.string().nullish(),
    PathTitle: z.string(),
    PathGUID: z.string().nullish(),
    Confidence: z.string().nullish(),
    Type: z.string().nullish(),
    catalog_data: catalogData,
  })
  .passthrough();

const recommendationResponse = z.array(recommendationType);
const recommendationPathResponse = z.array(recommendationPath);

export async function podcasts() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsPodcasts`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function courses() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsCourses`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function challenges() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsChallenges`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function paths() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsPaths`,
    {}
  );

  return recommendationPathResponse.parse(data);
}

export async function pathsByTopics(topics: string[], excludeGuids: string[]) {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsPaths`,
    {
      topics: topics,
      exclude_guids: excludeGuids,
    }
  );

  return recommendationPathResponse.parse(data);
}

export async function events() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsWebinars`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function tutorials() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsTutorials`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function videos() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsVideos`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function instructorLed() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsIlts`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function practiceExams() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsPracticeExams`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function labs() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsLabs`,
    {}
  );

  return recommendationResponse.parse(data);
}

export async function certificationRecommendedPaths(guids: Array<string>) {
  const { data } = await http().post(
    `${window.env.API_CATALOG}/retrieveCatalogItems`,
    {
      type: "path",
      Guids: guids,
    }
  );
  return data;
}

export async function getTopOfFunnelRecommendations(certPreference: string) {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetTopOfFunnelRecommendations`,
    {
      certPreference: certPreference,
      recommendationLimit: "1",
    }
  );

  return data;
}

export async function checkCeCredits(pathGuid: string) {
  const { data } = await http().get(
    `${window.env.API_APOLLO_MIDDLEWARE}/user-activities?pathGuid=${pathGuid}`
  );

  return data;
}

export async function ceEligibility(acronym: string) {
  const { data } = await http().get(
    `${window.env.API_APOLLO_MIDDLEWARE}/eligibility?courseGroup=${acronym}`
  );

  return data;
}

export async function examVouchers() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsExamVouchers`,
    {}
  );

  // return data;
  return validateZod(data);
}

export async function bundles() {
  const { data } = await http().post(
    `${window.env.API_APOLLO_MIDDLEWARE}/GetRecommendationsBundles`,
    {}
  );

  // Add bundle type to catalog_data to match pattern of other content types
  const updatedData = data.map((bundle: any) => ({
    ...bundle,
    catalog_data: {
      ...bundle.catalog_data,
      type: "bundle",
    },
  }));

  return validateZod(updatedData);
}

export function validateZod(data: any) {
  try {
    return recommendationResponse.parse(data);
  } catch (e) {
    if (e instanceof z.ZodError) {
      e.errors.forEach((error: { path: any[]; message: any }) => {
        console.log("This is a Zod validation error");
        console.warn(`${error.path.join(".")} - ${error.message}`);
        console.log("Zod error end!");
      });
    } else {
      console.log("An unexpected zod error occurred:", e);
    }
  }
}
